import apiInstance from "api";
import afmsApiInstance from "afms/api";

// Vendor Access Management
export const VendorAccessService = {
  // Vendor Registration
  registerAsVendor: async (data) => {
    return await apiInstance.post("/vendor-access/register-as-vendor", data);
  },
  getVendorProfile: async () => {
    return await apiInstance.get("/vendor-access/vendor-profile");
  },
  updateVendorProfile: async (data) => {
    return await apiInstance.put("/vendor-access/vendor-profile", data);
  },

  // Client Org Admin - Vendor Search & Management
  searchVendors: async (params) => {
    return await apiInstance.get("/vendor-access/search", { params });
  },
  inviteVendor: async (data) => {
    return await apiInstance.post("/vendor-access/invite", data);
  },
  listVendors: async () => {
    return await apiInstance.get("/vendor-access/vendors");
  },
  revokeAccess: async (grantId) => {
    return await apiInstance.put(`/vendor-access/${grantId}/revoke`);
  },
  updateGrant: async (grantId, data) => {
    return await apiInstance.put(`/vendor-access/${grantId}/update`, data);
  },

  // Shared Asset Management
  getSharedAssets: async (grantId) => {
    return await apiInstance.get(`/vendor-access/${grantId}/shared-assets`);
  },
  updateSharedAssets: async (grantId, data) => {
    return await apiInstance.put(`/vendor-access/${grantId}/shared-assets`, data);
  },

  // Recycling materials catalog (Used Oil, AntiFreeze, …).
  recyclingTypes: async () => {
    return await apiInstance.get("/recycling-types");
  },
  // Recycling vendor organizations, optionally filtered to those that handle a
  // given recycling material. `connectedOnly` limits the list to vendors already
  // connected to the caller's org (the pickup-request popup); the asset's
  // default vendor is passed as `includeVendorOrganizationId` so it never drops
  // out of that list.
  recyclingVendors: async (recyclingTypeId, { connectedOnly = false, includeVendorOrganizationId } = {}) => {
    const params = {};
    if (recyclingTypeId != null) params.recyclingTypeId = recyclingTypeId;
    if (connectedOnly) params.connectedOnly = true;
    if (includeVendorOrganizationId != null) {
      params.includeVendorOrganizationId = includeVendorOrganizationId;
    }
    return await apiInstance.get("/vendor-access/recycling-vendors", { params });
  },

  // Vendor Org - Client Management
  listClients: async () => {
    return await apiInstance.get("/vendor-access/clients");
  },
  acceptInvitation: async (grantId) => {
    return await apiInstance.put(`/vendor-access/${grantId}/accept`);
  },
  declineInvitation: async (grantId) => {
    return await apiInstance.put(`/vendor-access/${grantId}/decline`);
  },
};

// Vendor Portal Data
export const VendorPortalService = {
  getDevices: async (clientOrgId, deviceTypes) => {
    const params = {};
    if (clientOrgId) params.clientOrgId = clientOrgId;
    if (deviceTypes && deviceTypes.length > 0) params.deviceTypes = deviceTypes.join(",");
    return await apiInstance.get("/vendor-portal/devices", { params });
  },
  getTickets: async () => {
    return await apiInstance.get("/vendor-portal/tickets");
  },
  updateTicket: async (ticketId, data) => {
    return await apiInstance.put(`/vendor-portal/tickets/${ticketId}/update`, data);
  },
  getDashboard: async () => {
    return await apiInstance.get("/vendor-portal/dashboard");
  },
  getClientLocationTree: async (clientOrgId) => {
    return await apiInstance.get("/internal/locations/tree", {
      params: { organizationId: clientOrgId },
    });
  },
  getClientUsers: async (clientOrgId) => {
    return await apiInstance.get("/vendor-portal/client-users", {
      params: { clientOrgId },
    });
  },
  getClientAssets: async (clientOrgId, locationId) => {
    const params = { organizationId: clientOrgId };
    if (locationId) params.locationId = locationId;
    return await apiInstance.get("/internal/assets", { params });
  },
  assignVendorToTicket: async (ticketId, vendorOrganizationId) => {
    return await apiInstance.post(`/tickets/${ticketId}/assign-vendor`, { vendorOrganizationId });
  },
};

// Vendor Monitoring (AFMS)
export const VendorMonitoringService = {
  // `config` is passed through to axios (e.g. a per-call `timeout`).
  getTanks: async (clientOrgId, config = {}) => {
    const params = clientOrgId ? { clientOrgId } : {};
    return await afmsApiInstance.get("/vendor-monitoring/tanks", { ...config, params });
  },
  getBins: async (clientOrgId, config = {}) => {
    const params = clientOrgId ? { clientOrgId } : {};
    return await afmsApiInstance.get("/vendor-monitoring/bins", { ...config, params });
  },
  createAlert: async (data) => {
    return await afmsApiInstance.post("/vendor-monitoring/alerts", data);
  },
  getAlerts: async (clientOrgId) => {
    const params = clientOrgId ? { clientOrgId } : {};
    return await afmsApiInstance.get("/vendor-monitoring/alerts", { params });
  },
  acknowledgeAlert: async (alertId) => {
    return await afmsApiInstance.put(`/vendor-monitoring/alerts/${alertId}/ack`);
  },
  getDashboard: async () => {
    return await afmsApiInstance.get("/vendor-monitoring/dashboard");
  },
  getSiteOverview: async (clientOrgId) => {
    return await afmsApiInstance.get("/vendor-monitoring/sites", { params: { clientOrgId } });
  },
  getTankDetail: async (deviceNumber) => {
    return await afmsApiInstance.get(`/vendor-monitoring/tanks/${encodeURIComponent(deviceNumber)}`);
  },
  getTankLevel: async (deviceNumber) => {
    return await afmsApiInstance.get(`/vendor-monitoring/tanks/${encodeURIComponent(deviceNumber)}/level`);
  },
  getLevelHistory: async (deviceNumber, params) => {
    return await afmsApiInstance.get(`/vendor-monitoring/tanks/${encodeURIComponent(deviceNumber)}/level/history`, { params });
  },
  getTankEvents: async (deviceNumber, params) => {
    return await afmsApiInstance.get(`/vendor-monitoring/tanks/${encodeURIComponent(deviceNumber)}/events`, { params });
  },

  // Fill-level history for the Levels-view chart popup. Keyed by monitor id
  // rather than device number: a manually monitored tank has no device number
  // at all, so the by-device routes above can't reach one. These are the
  // vendor-scoped mirrors of the client's /tanks/{id}/… and /bins/{id}/events
  // — the client routes scope by the CALLER's org, which for a vendor user is
  // the vendor's own org and never the client's.
  getLevelHistoryByTankId: async (tankMonitorId, params) => {
    return await afmsApiInstance.get(
      `/vendor-monitoring/tanks/by-id/${tankMonitorId}/level/history`, { params });
  },
  getManualReadingsByTankId: async (tankMonitorId, params) => {
    return await afmsApiInstance.get(
      `/vendor-monitoring/tanks/by-id/${tankMonitorId}/manual-readings`, { params });
  },
  getBinEvents: async (binMonitorId) => {
    return await afmsApiInstance.get(`/vendor-monitoring/bins/${binMonitorId}/events`);
  },
  schedulePickup: async (data) => {
    return await afmsApiInstance.post("/vendor-monitoring/schedule-pickup", data);
  },
  getReportByRoute: async ({ from, to, routeStatus, clientOrgId } = {}) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (routeStatus != null && routeStatus !== "") params.routeStatus = routeStatus;
    if (clientOrgId) params.clientOrgId = clientOrgId;
    return await afmsApiInstance.get("/vendor-monitoring/report/by-route", { params });
  },
};
